/**
 * ElevenLabs API Service
 * 文档: https://elevenlabs.io/docs/api-reference
 * 鉴权头: xi-api-key（非 Bearer）
 */

/** dev 专用同源代理路径：由 vite 代理转发到 api.elevenlabs.io，绕开 localhost 的浏览器跨域 */
const DEV_PROXY_BASE_URL = '/api/elevenlabs'
/** ElevenLabs 官方地址：生产环境浏览器可直连（实测 CORS 放行 *），无需服务端中转 */
export const ELEVENLABS_OFFICIAL_BASE_URL = 'https://api.elevenlabs.io'
const DEFAULT_MODEL_ID = 'eleven_multilingual_v2'

export interface ElevenLabsModel {
  id: string
  name: string
  provider: 'elevenlabs'
  description?: string
  contextLength: number
  deprecated: boolean
}

/**
 * 归一 ElevenLabs 请求地址（对齐 MiniMax/Fish：dev 走代理、生产直连官方）：
 * - dev：空 或 官方地址 → vite 代理 /api/elevenlabs（绕开 localhost 跨域）；自建地址原样用
 * - 生产：空 → 官方地址直连；填了自建代理地址则原样用
 */
export function resolveBaseUrl(baseUrl?: string): string {
  const trimmed = baseUrl?.trim().replace(/\/+$/, '')
  if (import.meta.env.DEV) {
    if (!trimmed || trimmed === ELEVENLABS_OFFICIAL_BASE_URL) {
      return DEV_PROXY_BASE_URL
    }
    return trimmed
  }
  return trimmed || ELEVENLABS_OFFICIAL_BASE_URL
}

async function parseError(response: Response): Promise<Record<string, unknown>> {
  const errorText = await response.text()
  try {
    return JSON.parse(errorText) as Record<string, unknown>
  }
  catch {
    return { message: errorText }
  }
}

/** 上传音频克隆声音 → 返回 voice_id */
export async function createVoiceClone(params: {
  file: File
  name: string
  apiKey: string
  baseUrl?: string
}): Promise<{ voiceId: string, requiresVerification: boolean }> {
  const { file, name, apiKey, baseUrl } = params

  const formData = new FormData()
  formData.append('name', name)
  formData.append('files', file)

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/v1/voices/add`, {
    method: 'POST',
    headers: { 'xi-api-key': apiKey },
    body: formData,
  })

  if (!response.ok) {
    const err = await parseError(response)
    if (response.status === 401)
      throw new Error('API Key 无效，请检查后重试')
    if (response.status === 402)
      throw new Error('余额不足或无克隆权限，请检查账户')
    if (response.status === 422)
      throw new Error(`参数错误: ${String(err.detail ?? err.message ?? response.status)}`)
    throw new Error(`${String(err.message ?? '克隆失败')}: ${response.status}`)
  }

  const data = await response.json() as { voice_id: string, requires_verification?: boolean }
  return { voiceId: data.voice_id, requiresVerification: !!data.requires_verification }
}

/** 用 voice_id 合成语音 → 返回音频二进制 */
export async function generateSpeech(params: {
  text: string
  voiceId: string
  apiKey: string
  baseUrl?: string
  modelId?: string
}): Promise<ArrayBuffer> {
  const { text, voiceId, apiKey, baseUrl, modelId = DEFAULT_MODEL_ID } = params

  if (!text?.trim())
    throw new Error('文本内容为空')
  if (!voiceId)
    throw new Error('未指定 voice_id')

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/v1/text-to-speech/${voiceId}`, {
    method: 'POST',
    headers: {
      'xi-api-key': apiKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text: text.trim(), model_id: modelId }),
  })

  if (!response.ok) {
    const err = await parseError(response)
    if (response.status === 401)
      throw new Error('API Key 无效')
    if (response.status === 402)
      throw new Error('余额不足，请充值后再试')
    if (response.status === 422)
      throw new Error(`参数错误: ${String(err.detail ?? err.message ?? response.status)}`)
    throw new Error(`${String(err.message ?? '语音合成失败')}: ${response.status}`)
  }

  return await response.arrayBuffer()
}

/** 列出可用于 TTS 的模型 */
export async function listModels(params: {
  apiKey: string
  baseUrl?: string
}): Promise<ElevenLabsModel[]> {
  const { apiKey, baseUrl } = params

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/v1/models`, {
    headers: { 'xi-api-key': apiKey },
  })

  if (!response.ok) {
    if (response.status === 401)
      throw new Error('API Key 无效')
    throw new Error(`获取模型列表失败: ${response.status}`)
  }

  const data = await response.json() as Array<{
    model_id: string
    name?: string
    description?: string
    can_do_text_to_speech?: boolean
  }>

  return data
    .filter(m => m.can_do_text_to_speech === true)
    .map(m => ({
      id: m.model_id,
      name: m.name || m.model_id,
      provider: 'elevenlabs' as const,
      description: m.description,
      contextLength: 0,
      deprecated: false,
    }))
}

/** 校验 voice_id 是否存在并带出名字/试听 */
export async function getVoice(params: {
  voiceId: string
  apiKey: string
  baseUrl?: string
}): Promise<{ voiceId: string, name: string, previewUrl?: string, category?: string }> {
  const { voiceId, apiKey, baseUrl } = params

  const response = await fetch(`${resolveBaseUrl(baseUrl)}/v1/voices/${voiceId}`, {
    headers: { 'xi-api-key': apiKey },
  })

  if (!response.ok) {
    if (response.status === 401)
      throw new Error('API Key 无效')
    if (response.status === 404)
      throw new Error('未找到该 voice_id，请确认后重试')
    throw new Error(`校验 voice 失败: ${response.status}`)
  }

  const data = await response.json() as { voice_id: string, name: string, preview_url?: string, category?: string }
  return {
    voiceId: data.voice_id,
    name: data.name,
    previewUrl: data.preview_url,
    category: data.category,
  }
}
